import { Fragment, useState, useMemo, useEffect } from 'react';
import type { ExportDestinationContent, SampleBatch, JobTypeVersionContent, TransformChainStep, Behavior, ExportFormatStructureNode } from '@capture/shared';
import type { ExportColumn } from '../../hooks/useExportFormats';
import {
  LITERAL_SOURCE_PREFIX,
  isLiteralSource,
  literalSourceValue,
  normalizeTransforms,
  chainStepToBehavior,
  findCrossFieldOps,
  computeSlotCoverage,
  structureSlots,
} from '@capture/shared';
import { ExportPreview } from './ExportPreview';
import { DestinationWarnings } from './DestinationWarnings';
import { MappingRowFormatEditor } from './MappingRowFormatEditor';
import { useDestinationValidator } from '../hooks/useDestinationValidator';
import { TransformChipStack } from './components/TransformChipStack';
import { TransformChainDrawer } from './components/TransformChainDrawer';
import { NamedChainPicker } from './components/NamedChainPicker';
import { PromoteToCanonicalDialog, type DialogState, type NamedChainError, type PromoteConfirm } from './components/PromoteToCanonicalDialog';
import { usePromoteToCanonicalContext } from './hooks/usePromoteToCanonicalContext';
import { useFormConfigStore } from '../state/store';
import { namedChainsApi, type NamedChainRow } from '@/features/admin/named-chains/api/named-chains-client';
import { getDossierDocTypeSchema } from './api/dossier-lookup-client';
import type { DossierDocTypeField } from './api/dossier-lookup-client';
import { ConfirmModal } from '@/components/ConfirmModal';
import { toast } from '@/components/Toaster';
import styles from './export-composer.module.css';

// F-026 — normalize a name for fuzzy auto-map matching: lowercase and strip
// every non-alphanumeric character so `EMPLOYER` ≈ `employer`,
// `Notice Date` ≈ `notice_date`, `Employer-ID` ≈ `employer_id`, etc.
function normalizeName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

type Mapping = ExportDestinationContent['mappings'][number];

// Slice 4c-2 — a constrained row flags itself when its slot is required but the
// row is unfulfilled. Resolves the row's slot via name→path, then reads coverage.
type RowSlotState = { slot: { required: boolean }; fulfilled: boolean };
function isRowUnfulfilled(
  targetColumn: string,
  slotNameToPath: Map<string, string>,
  perSlotByPath: Map<string, RowSlotState>,
): boolean {
  const path = slotNameToPath.get(targetColumn);
  const state = path ? perSlotByPath.get(path) : undefined;
  return !!state && state.slot.required && !state.fulfilled;
}

function makeEmptyContent(): JobTypeVersionContent {
  return {
    schemaVersion: 2,
    fields: [],
    pipeline: { stages: [] },
    headerMapping: { format: 'none', paths: {} },
    rules: [],
    export: { destinations: [] },
  };
}

// System-field source picker entries. Order is what the operator sees in the
// dropdown — keep the most-commonly-mapped ones near the top.
//
// R-167 WU-8 — `_displayId` and `_customDisplayLabel` are the canonical
// document identifiers. `_customDisplayLabel` falls back to `_displayId` when
// the label was never stamped (legacy / pre-R-167 documents), so it's the
// safe choice for the "doc number" column when a tenant has any custom
// templates configured anywhere in the JobType tree. `_displayId` remains
// the explicit option for tenants who want the machine identifier regardless.
const SPECIAL_FIELDS = [
  '_documentId',
  '_batchName',
  '_classification',
  '_pageCount',
  '_exportTimestamp',
  '_operatorName',
  '_imageUrls',
  '_displayId',
  '_customDisplayLabel',
] as const;

interface Props {
  mappings: ExportDestinationContent['mappings'];
  fieldNames: string[];
  format: ExportDestinationContent['format'];
  structure?: ExportDestinationContent['structure'];
  onChange: (mappings: ExportDestinationContent['mappings']) => void;
  sampleData: SampleBatch;
  jobTypeId?: string;
  /** Owning destination's id — needed by promote-to-canonical to know which dest to clear. */
  destinationId: string;
  /**
   * R-201 — the selected ExportFormat's columns. When provided (including an
   * empty array), the panel is CONSTRAINED: it renders one row per column with a
   * fixed (non-editable) target column and an editable source-field select.
   * Columns are owned by the ExportFormat (clone/edit it to change them); the
   * panel never edits columns. An EMPTY array means "no format selected" and a
   * "Pick a format first" prompt replaces the grid.
   *
   * `undefined` is the legacy/unconstrained mode (free-text targets + add/remove
   * rows) retained for callers that don't bind a format. The live editor
   * (DestinationDetail) always passes a constrained value.
   */
  formatColumns?: ExportColumn[];
  /**
   * F-036 — for a dossier destination, the configured remote doc-type name.
   * When set, the (unconstrained) target column becomes a picker of that type's
   * fields (label + required marker), fetched from Dossier's internal schema
   * lookup, and a coverage hint flags unmapped required fields. `undefined` /
   * empty keeps the plain free-text target.
   */
  dossierTypeName?: string;
  /**
   * Slice 4c-1 — the bound format's structure tree. When present, the panel
   * renders a slot-coverage banner (`computeSlotCoverage`) so the operator sees
   * which required slots are still unfulfilled while mapping. The flat
   * `formatColumns` can't drive this (they lack required/allowEmpty). Absent for
   * flat/legacy/formatless destinations → no banner.
   */
  formatStructure?: ExportFormatStructureNode[];
  /**
   * E-040 — the name of the tenant `ExportFormat` this destination is
   * actually BOUND to (`selectedFormat.name` / `fmt.name` in the two
   * callers). Distinct from `constrained` (`formatColumns !== undefined`),
   * which is also true for sql's unbound inline-structure path — this prop
   * is only set when a real named format is selected, and switches the
   * top explainer from "create one first" guidance to a statement of what
   * IS bound. Absent/undefined keeps the create-first guidance.
   */
  boundFormatName?: string;
}

export function MappingPanel({ mappings, fieldNames, format, structure, sampleData, onChange, jobTypeId, destinationId, formatColumns, dossierTypeName, formatStructure, boundFormatName }: Props) {
  // R-201 — when a format is selected, the panel is constrained to its columns.
  const constrained = formatColumns !== undefined;

  // Slice 4c — resolve a row's targetColumn (leaf name) → its slot path. Built
  // once from the format structure; reused by the coverage bridge, the row
  // derivation (persist slotPath), and the per-row inline flag.
  const slotNameToPath = useMemo(
    () => new Map((formatStructure ? structureSlots(formatStructure) : []).map((s) => [s.name, s.path] as const)),
    [formatStructure],
  );

  // Feature A — path → slot descriptor (for allowEmpty lookup per row).
  const slotByPath = useMemo(
    () => new Map((formatStructure ? structureSlots(formatStructure) : []).map((s) => [s.path, s] as const)),
    [formatStructure],
  );

  // Slice 4c-1 — slot coverage over the live mappings (literal source counts as
  // fulfilled, blank as unfulfilled — see toSlotFulfillment). Null when the
  // format has no structure (flat/legacy) so the banner is suppressed.
  //
  // Bridge: today's mappings are keyed by leaf NAME (targetColumn), but slots are
  // keyed by full PATH. Map each legacy mapping's name → its slot path so nested
  // formats count correctly; an explicit `slotPath` (Slice 4c-2) wins when set.
  const coverage = useMemo(() => {
    if (!formatStructure || formatStructure.length === 0) return null;
    const pathed = mappings.map((m) => ({
      ...m,
      slotPath: m.slotPath ?? slotNameToPath.get(m.targetColumn) ?? m.targetColumn,
    }));
    return computeSlotCoverage(formatStructure, pathed);
  }, [formatStructure, mappings, slotNameToPath]);

  // Slice 4c-2 — per-row inline fulfillment status keyed by slot path.
  const perSlotByPath = useMemo(
    () => new Map((coverage?.perSlot ?? []).map((p) => [p.slot.path, p] as const)),
    [coverage],
  );

  // R-201 — the rows actually rendered. In constrained mode they are derived
  // from the format columns (one per column, in column order, joined to any
  // existing mapping by name so transforms/overrides survive) so the grid
  // reflects the format immediately, even before the parent's re-key onChange
  // round-trips back through props. All row-index handlers below operate against
  // `rows` and persist the full derived list, so mutating one row never drops
  // the others.
  const rows: ExportDestinationContent['mappings'] = useMemo(() => {
    if (!formatColumns) return mappings;
    const byName = new Map(mappings.map((m) => [m.targetColumn, m] as const));
    return formatColumns.map((col) => {
      const existing = byName.get(col.name);
      // Slice 4c-2 — persist the slot path so the synced fulfillment is
      // path-keyed (read by the gate/worker). Backfilled onto reused mappings.
      const slotPath = slotNameToPath.get(col.name);
      if (existing) {
        return slotPath && existing.slotPath !== slotPath ? { ...existing, slotPath } : existing;
      }
      return { targetColumn: col.name, sourceField: '', ...(slotPath ? { slotPath } : {}) };
    });
  }, [formatColumns, mappings, slotNameToPath]);

  // Persist the re-keyed rows back to the parent when the format columns change
  // (added/removed/reordered). `rows` is the single source of truth for the
  // derivation; we only fire onChange when the stored `mappings` actually differ
  // (count, or any row that isn't the same object the join reused) so we don't
  // loop. Keyed on `formatColumns` only — `mappings` settles after the parent
  // applies the change.
  useEffect(() => {
    if (!formatColumns) return;
    const drifted =
      rows.length !== mappings.length || rows.some((r, i) => r !== mappings[i]);
    // F-055 / F-056 residual — never persist a derivation that carries NOTHING
    // worth persisting. On mount the re-key derives one row per format column;
    // for a flat format with no mappings those rows are pure blanks, and firing
    // onChange with them marks a freshly-loaded PUBLISHED version dirty, which
    // trips useStoreAutoFork → a spurious new draft → the DraftBanner re-shows
    // "draft" right after a successful publish (re-publish churn). A row is worth
    // persisting when it carries real data (sourceField / kind / constantValue)
    // OR a structural slotPath (Slice 4c-2 path-keyed fulfillment). targetColumn
    // alone doesn't count. When any such row exists the re-key still fires
    // (reorder/rename/drop, slotPath backfill) exactly as before.
    const hasContent = rows.some(
      (r) => Boolean(r.sourceField) || r.kind != null || Boolean(r.constantValue) || Boolean(r.slotPath),
    );
    if (drifted && hasContent) onChange(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatColumns]);

  // F-036 — fetch the remote dossier doc-type field schema so the target column
  // can be a picker (label + required) instead of a guess-the-key free-text box.
  const [dossierFields, setDossierFields] = useState<DossierDocTypeField[] | null>(null);
  useEffect(() => {
    const name = dossierTypeName?.trim();
    if (!name) {
      setDossierFields(null);
      return;
    }
    let cancelled = false;
    getDossierDocTypeSchema(name)
      .then((schema) => {
        if (!cancelled) setDossierFields(schema.fields);
      })
      .catch(() => {
        // Lookup failure degrades gracefully to free-text targets.
        if (!cancelled) setDossierFields(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dossierTypeName]);

  // Required remote fields not yet mapped to any target (coverage hint).
  const uncoveredRequired = useMemo(() => {
    if (!dossierFields) return [];
    const mappedTargets = new Set(mappings.map((m) => m.targetColumn).filter(Boolean));
    return dossierFields.filter((f) => f.required && !mappedTargets.has(f.key));
  }, [dossierFields, mappings]);

  const [drawerForRowIndex, setDrawerForRowIndex] = useState<number | null>(null);
  // R-50 / WU-2.3: per-row format-override editor expansion state. One row at
  // a time keeps the table dense and avoids the operator losing track of which
  // override they're editing.
  const [formatEditorForRowIndex, setFormatEditorForRowIndex] = useState<number | null>(null);
  // R-46 — collapsed on every viewport by default. A width-conditional
  // default still left the preview EXPANDED (and ~half-width, crushing
  // the Source column) on normal maximized windows, which is the bug.
  const [previewCollapsed, setPreviewCollapsed] = useState(true);

  const sourceUsageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of rows) {
      if (m.sourceField) {
        counts.set(m.sourceField, (counts.get(m.sourceField) ?? 0) + 1);
      }
    }
    return counts;
  }, [rows]);

  function handleSaveChain(rowIndex: number, chain: Array<{ type: string; config: Record<string, unknown> }>) {
    const next = rows.map((m, i) =>
      i === rowIndex ? { ...m, transforms: { kind: 'inline' as const, steps: chain } } : m
    );
    onChange(next);
    setDrawerForRowIndex(null);
  }

  /** Switch mapping at rowIndex to named mode, pointing to the given namedChainId. */
  // F-035 — attach an EXISTING named chain to a fresh/inline row. The picker
  // used to render only when the row was ALREADY named, so chain reuse (the
  // feature's purpose) was unreachable: every row had to re-author + promote
  // its own copy. Lazy: chains fetch on first click, not per row render.
  function UseNamedChainAffordance({ rowIndex }: { rowIndex: number }) {
    const [open, setOpen] = useState(false);
    const [chains, setChains] = useState<NamedChainRow[] | null>(null);
    if (!open) {
      return (
        <button
          type="button"
          className={styles.useNamedChainBtn ?? ''}
          style={{ fontSize: 'var(--text-xs, 0.8125rem)', whiteSpace: 'nowrap' }}
          onClick={() => {
            setOpen(true);
            void namedChainsApi
              .list()
              .then((res) => setChains(res.chains))
              .catch(() => setChains([]));
          }}
        >
          Use named chain…
        </button>
      );
    }
    return (
      <select
        aria-label="Use a named chain"
        value=""
        disabled={chains === null}
        style={{ fontSize: 'var(--text-xs, 0.8125rem)' }}
        onChange={(e) => {
          if (e.target.value) handleSwitchToNamed(rowIndex, e.target.value);
        }}
      >
        <option value="">{chains === null ? 'Loading…' : '— pick a named chain —'}</option>
        {(chains ?? []).map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    );
  }

  function handleSwitchToNamed(rowIndex: number, namedChainId: string) {
    const next = rows.map((m, i) =>
      i === rowIndex ? { ...m, transforms: { kind: 'named' as const, namedChainId } } : m
    );
    onChange(next);
  }

  /** Switch mapping at rowIndex from named mode back to inline, pre-seeded with seedSteps. */
  function handleSwitchToInline(rowIndex: number, seedSteps: Array<{ type: string; config: Record<string, unknown> }>) {
    const next = rows.map((m, i) =>
      i === rowIndex ? { ...m, transforms: { kind: 'inline' as const, steps: seedSteps } } : m
    );
    onChange(next);
  }

  /** After "Save as named chain": flip the row to named mode with the new chain id. */
  function handleSavedAsNamed(rowIndex: number, namedChainId: string) {
    handleSwitchToNamed(rowIndex, namedChainId);
    setDrawerForRowIndex(null);
  }

  // Promote-to-canonical orchestration state
  const [promoteState, setPromoteState] = useState<DialogState | null>(null);
  const [promoteRowIndex, setPromoteRowIndex] = useState<number | null>(null);
  const [promoteResolvedSteps, setPromoteResolvedSteps] = useState<TransformChainStep[]>([]);
  const [promoteNamedChainError, setPromoteNamedChainError] = useState<NamedChainError | null>(null);
  const [promoteOriginalChain, setPromoteOriginalChain] = useState<TransformChainStep[]>([]);
  const [promoteSourceFieldName, setPromoteSourceFieldName] = useState<string>('');

  const formContent = useFormConfigStore((s) => s.content);
  const updateField = useFormConfigStore((s) => s.updateField);
  const destinationValidationErrors = useDestinationValidator(destinationId);

  const promoteContext = usePromoteToCanonicalContext({
    // Merge with empty content as a safe fallback — partial mocks (e.g. tests) may omit fields/export
    content: { ...makeEmptyContent(), ...formContent },
    sourceFieldName: promoteSourceFieldName,
    sourceDestinationId: destinationId,
    resolvedSteps: promoteResolvedSteps,
  });

  async function startPromote(rowIndex: number, currentChain: TransformChainStep[]) {
    const row = rows[rowIndex];
    if (!row) return;
    const sourceFieldName = row.sourceField;
    if (!sourceFieldName) return;

    setPromoteRowIndex(rowIndex);
    setPromoteSourceFieldName(sourceFieldName);
    setPromoteOriginalChain(currentChain);
    setPromoteNamedChainError(null);

    // Determine if the current row is a named-chain ref
    const raw = row.transforms as unknown;
    const isNamedRef =
      raw !== undefined && raw !== null && !Array.isArray(raw) &&
      typeof raw === 'object' && (raw as { kind?: string }).kind === 'named';

    let resolvedSteps: TransformChainStep[];
    if (isNamedRef) {
      const namedChainId = (raw as { namedChainId: string }).namedChainId;
      try {
        const chain = await namedChainsApi.getById(namedChainId);
        resolvedSteps = chain.steps as TransformChainStep[];
      } catch (err) {
        const status = (err as { status?: number }).status ?? 500;
        setPromoteNamedChainError({ namedChainId, status });
        setPromoteState('named-chain-error');
        return;
      }
    } else {
      // Inline / legacy bare-array — drawer's chain is already the resolved steps
      resolvedSteps = currentChain;
    }

    setPromoteResolvedSteps(resolvedSteps);
    const offenders = findCrossFieldOps(resolvedSteps);
    setPromoteState(offenders.length > 0 ? 'hard-block' : 'mergeable');
  }

  function closePromote() {
    setPromoteState(null);
    setPromoteRowIndex(null);
    setPromoteResolvedSteps([]);
    setPromoteNamedChainError(null);
    setPromoteOriginalChain([]);
    setPromoteSourceFieldName('');
  }

  async function retryPromote() {
    if (promoteRowIndex !== null) {
      await startPromote(promoteRowIndex, promoteOriginalChain);
    }
  }

  function confirmPromote(confirm: PromoteConfirm) {
    if (!formContent || promoteRowIndex === null || !promoteSourceFieldName) {
      closePromote();
      return;
    }

    const newBehaviors: Behavior[] = promoteResolvedSteps.map(chainStepToBehavior);
    const existing = promoteContext.existingPostOcr;
    const merged = confirm.position === 'prepend'
      ? [...newBehaviors, ...existing]
      : [...existing, ...newBehaviors];

    const field = formContent.fields.find((f) => f.name === promoteSourceFieldName);
    if (!field) {
      closePromote();
      return;
    }

    updateField(promoteSourceFieldName, {
      stageMap: { ...field.stageMap, 'post-ocr': merged },
    });

    const next = rows.map((m, i) =>
      i === promoteRowIndex ? { ...m, transforms: [] } : m
    );
    onChange(next);
    setDrawerForRowIndex(null);  // close drawer too
    closePromote();
  }

  function handleTargetChange(index: number, value: string) {
    const next = rows.map((m, i) =>
      i === index ? { ...m, targetColumn: value } : m
    );
    onChange(next);
  }

  function handleSourceChange(index: number, value: string) {
    const next = rows.map((m, i) => {
      if (i !== index) return m;
      // Feature A — when the source field picker changes, carry the current
      // row kind forward (so an explicit kind declaration is not silently lost).
      // For field/calculated, keep the existing kind (or default to "field").
      const currentKind = m.kind ?? (isLiteralSource(m.sourceField) ? 'constant' : 'field');
      const updated: Mapping = { ...m, sourceField: value, kind: currentKind };
      // Switching TO the literal prefix sentinel via the picker means the operator
      // chose "Literal value…" — treat as constant.
      if (value === LITERAL_SOURCE_PREFIX) {
        updated.kind = 'constant';
        updated.constantValue = '';
      } else if (!isLiteralSource(value)) {
        // Moving away from literal prefix — clear constantValue.
        delete updated.constantValue;
      }
      return updated;
    });
    onChange(next);
  }

  // Feature A — per-row kind selector handler. Writes the kind→storage contract.
  function handleKindChange(index: number, kind: string) {
    const next = rows.map((m, i) => {
      if (i !== index) return m;
      const updated: Mapping = { ...m, kind: kind as Mapping['kind'] };
      switch (kind) {
        case 'field':
        case 'calculated':
          // Clear constant-specific fields; keep any existing sourceField if it's
          // not a literal (field pickers preserve the previous selection).
          if (isLiteralSource(m.sourceField)) updated.sourceField = '';
          delete updated.constantValue;
          break;
        case 'constant':
          // Seed constant: extract existing literal value if present, else empty.
          updated.constantValue = isLiteralSource(m.sourceField)
            ? literalSourceValue(m.sourceField)
            : '';
          updated.sourceField = LITERAL_SOURCE_PREFIX + (updated.constantValue ?? '');
          break;
        case 'empty':
          updated.sourceField = '';
          delete updated.constantValue;
          break;
      }
      return updated;
    });
    onChange(next);
  }

  // Feature A — update the constant value for a row (keeps kind:"constant" +
  // writes both constantValue and __literal: sourceField).
  function handleConstantValueChange(index: number, value: string) {
    const next = rows.map((m, i) =>
      i === index
        ? { ...m, kind: 'constant' as const, constantValue: value, sourceField: LITERAL_SOURCE_PREFIX + value }
        : m,
    );
    onChange(next);
  }

  // Feature A — derive the display kind for a row's kind <select> value.
  function rowKind(m: Mapping): string {
    if (m.kind) return m.kind;
    if (isLiteralSource(m.sourceField)) return 'constant';
    return 'field';
  }

  // Feature A — look up whether a row's slot allows empty, defaulting true for
  // ad-hoc rows (no slot info).
  function rowAllowsEmpty(m: Mapping): boolean {
    const path = m.slotPath ?? slotNameToPath.get(m.targetColumn);
    if (!path) return true; // ad-hoc / no slot info
    const slot = slotByPath.get(path);
    return slot ? slot.allowEmpty : true;
  }

  function handleDelete(index: number) {
    onChange(mappings.filter((_, i) => i !== index));
  }

  function handleAddRow() {
    onChange([...mappings, { targetColumn: '', sourceField: '' }]);
  }

  function handleAutoMap() {
    if (formatColumns) {
      // R-201 — column-driven: each format column gets a matching source field.
      // F-026 — match exact first, then normalized (case/separator-insensitive),
      // since target column names rarely equal field internal names byte-for-byte.
      const exact = new Set(fieldNames);
      const byNormalized = new Map<string, string>();
      for (const f of fieldNames) {
        const n = normalizeName(f);
        // First field wins on a normalized collision (stable, predictable).
        if (!byNormalized.has(n)) byNormalized.set(n, f);
      }
      let matched = 0;
      const next = formatColumns.map((col) => {
        let sourceField = '';
        if (exact.has(col.name)) {
          sourceField = col.name;
        } else {
          const hit = byNormalized.get(normalizeName(col.name));
          if (hit) sourceField = hit;
        }
        if (sourceField) matched += 1;
        // E-024 — persist the structure's full slot path so nested reseeds sync a
        // matchable slotPath (auto-map previously emitted bare-name mappings only).
        const slotPath = slotNameToPath.get(col.name);
        return { targetColumn: col.name, sourceField, ...(slotPath ? { slotPath } : {}) };
      });
      onChange(next);
      // F-026 — report a result summary so a 0-match looks different from a
      // broken button.
      const total = formatColumns.length;
      if (matched === 0) {
        toast.info(`Auto-map: mapped 0 of ${total} columns — no field names matched. Set each source manually.`);
      } else if (matched < total) {
        toast.info(`Auto-map: mapped ${matched} of ${total} columns; ${total - matched} left unmatched.`);
      } else {
        toast.success(`Auto-map: mapped all ${total} columns.`);
      }
      return;
    }
    onChange(fieldNames.map((name) => ({ targetColumn: name, sourceField: name })));
    toast.success(`Auto-map: mapped all ${fieldNames.length} fields.`);
  }

  // R-186 (C): clicking "Auto-map all form fields" maps immediately when the
  // table is empty, but gates behind a confirm when it would overwrite existing
  // rows so the operator doesn't silently lose hand-tuned mappings.
  const [autoMapConfirmOpen, setAutoMapConfirmOpen] = useState(false);

  function requestAutoMap() {
    if (mappings.length > 0) {
      setAutoMapConfirmOpen(true);
      return;
    }
    handleAutoMap();
  }

  function confirmAutoMap() {
    setAutoMapConfirmOpen(false);
    handleAutoMap();
  }

  /**
   * R-49 + R-50 / WU-2.3: apply a partial patch to a single mapping row.
   * Used by the per-row format-override editor; `undefined` values prune the
   * key from the persisted row (rather than carrying empty shells through
   * the snapshot).
   */
  function handleFormatPatch(index: number, patch: Partial<Mapping>) {
    const next = rows.map((m, i) => {
      if (i !== index) return m;
      const merged = { ...m, ...patch };
      // Strip keys whose patch value was explicitly undefined so the
      // persistence layer doesn't carry `{ formatOverride: undefined }` shells.
      for (const key of Object.keys(patch) as Array<keyof Mapping>) {
        if (patch[key] === undefined) {
          delete (merged as Partial<Mapping>)[key];
        }
      }
      return merged;
    });
    onChange(next);
  }

  function hasFormatOverride(m: Mapping): boolean {
    return m.formatOverride != null;
  }

  return (
    <div className={styles.mappingPanel}>
      {/* Left panel — mapping table */}
      <div className={styles.mappingTableWrap}>
        {/* R-186 / E-040 — explain this is the per-JobType MAPPING layer,
            distinct from the tenant-wide format layer. State-aware: once a
            named format IS bound, say so instead of repeating "create one
            first" guidance the operator has already acted on. */}
        {boundFormatName ? (
          <p className={styles.mappingHelp} data-testid="mapping-help">
            Mapping this Job Type's fields to the columns of{' '}
            <strong>{boundFormatName}</strong>.
          </p>
        ) : (
          <p className={styles.mappingHelp} data-testid="mapping-help">
            <strong>Mapping</strong> = this Job Type's fields → an export format's
            columns. The <strong>format</strong> (file shape, delimiter, column
            order) is defined tenant-wide under{' '}
            <a href="/admin/export-formats">Export Formats</a> — create one there
            first, or use <strong>+ New format</strong> below.
          </p>
        )}
        {coverage && (
          <div
            className={styles.slotCoverageBanner}
            data-testid="slot-coverage-banner"
            data-accent={coverage.unfulfilledRequired.length > 0 ? 'warning' : 'success'}
          >
            <span className={styles.slotCoverageSummary}>
              {coverage.total} {coverage.total === 1 ? 'slot' : 'slots'}
              {coverage.requiredTotal > 0
                ? ` — ${coverage.requiredFulfilled}/${coverage.requiredTotal} required fulfilled`
                : ' — none required'}
            </span>
            {coverage.unfulfilledRequired.length > 0 && (
              <span className={styles.slotCoverageMissingLine}>
                Unfulfilled:{' '}
                {coverage.unfulfilledRequired.map((s) => (
                  <span key={s.path} className={styles.slotCoverageMissing}>
                    {s.name}
                  </span>
                ))}
              </span>
            )}
          </div>
        )}
        {constrained && (
          <p className={styles.mappingHelp} data-testid="constrained-help">
            Target columns are defined by the selected{' '}
            <a href="/admin/export-formats">Export Format</a>. To add, remove, or
            reorder columns, edit or <strong>clone</strong> the format. Here you
            map each column to one of this Job Type's source fields.
          </p>
        )}
        {/* F-026 — when the format defines fewer columns than the Job Type has
            fields, the operator's export coverage is silently capped. Point
            them at the format editor instead of leaving them confused. */}
        {constrained && rows.length > 0 && rows.length < fieldNames.length && (
          <p className={styles.mappingHelp} data-testid="short-format-hint">
            This format defines {rows.length}{' '}
            {rows.length === 1 ? 'column' : 'columns'}, but this Job Type has{' '}
            {fieldNames.length} fields. To export more fields,{' '}
            <a href="/admin/export-formats">edit the format</a> to add columns.
          </p>
        )}
        {/* F-036 — flag required Dossier doc-type fields that no mapping covers,
            so the operator maps them before export (an unmapped required field
            422s at delivery). */}
        {uncoveredRequired.length > 0 && (
          <p className={styles.mappingHelp} data-testid="dossier-coverage-hint">
            This Dossier type requires{' '}
            {uncoveredRequired.map((f, idx) => (
              <span key={f.key}>
                {idx > 0 ? ', ' : ''}
                <strong>{f.label}</strong>
              </span>
            ))}
            {' '}— add a mapping row targeting{' '}
            {uncoveredRequired.length === 1 ? 'it' : 'each'} or delivery may be rejected.
          </p>
        )}
        <div className={styles.mappingToolbar}>
          {!constrained && (
            <button
              type="button"
              className={styles.mappingAddBtn}
              onClick={handleAddRow}
            >
              + Add Row
            </button>
          )}
          {fieldNames.length > 0 && (
            <button
              type="button"
              className={styles.mappingAutoMapBtn}
              onClick={requestAutoMap}
            >
              + Auto-map all form fields
            </button>
          )}
        </div>

        {constrained && rows.length === 0 ? (
          <div className={styles.mappingEmptyState} data-testid="no-format-prompt">
            <p>
              Pick a format first. Select or create an export format for this
              destination, and its columns will appear here for mapping.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className={styles.mappingEmptyState}>
            <p>
              No fields are mapped. All source fields would be excluded — add a
              row or auto-map all form fields to begin.
            </p>
          </div>
        ) : (
          <table className={styles.mappingTable}>
            <thead>
              <tr>
                <th>Kind</th>
                <th>Source Field</th>
                <th>
                  Transform
                  <button
                    type="button"
                    className={styles.helpButton}
                    aria-label="About transforms"
                    title="Mapping-row transforms apply only to this destination. For tweaks every consumer should see, configure Post-OCR transforms on the field. Three tiers: Post-OCR (canonical) → Pre-Export compute (per-batch) → Mapping-row (this column)."
                  >
                    ?
                  </button>
                </th>
                <th className={styles.mappingThArrow}>→</th>
                <th>Target Column</th>
                <th className={styles.mappingThFormat} aria-label="Per-row format overrides">
                  Format
                </th>
                <th className={styles.mappingThAction} aria-label="Delete" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={i}>
                <tr>
                  {/* Feature A — explicit per-row kind selector */}
                  <td>
                    <select
                      className={styles.mappingSelect}
                      value={rowKind(m)}
                      onChange={(e) => handleKindChange(i, e.target.value)}
                      aria-label={`Source kind ${i + 1}`}
                    >
                      <option value="field">Field</option>
                      <option value="calculated">Calculated</option>
                      <option value="constant">Constant</option>
                      <option value="empty" disabled={!rowAllowsEmpty(m)}>
                        Empty{!rowAllowsEmpty(m) ? ' (not allowed)' : ''}
                      </option>
                    </select>
                  </td>
                  <td>
                    {rowKind(m) === 'constant' ? (
                      <input
                        type="text"
                        className={styles.mappingInput}
                        value={isLiteralSource(m.sourceField) ? literalSourceValue(m.sourceField) : (m.constantValue ?? '')}
                        onChange={(e) => handleConstantValueChange(i, e.target.value)}
                        placeholder="Constant value"
                        aria-label={`Constant value ${i + 1}`}
                      />
                    ) : rowKind(m) === 'empty' ? (
                      <span className={styles.mappingFixedTarget} aria-label={`Empty source ${i + 1}`}>— empty —</span>
                    ) : (
                      <select
                        className={styles.mappingSelect}
                        value={m.sourceField}
                        onChange={(e) => handleSourceChange(i, e.target.value)}
                        aria-label={`Source field ${i + 1}`}
                      >
                        <option value="">— select field —</option>
                        <option value={LITERAL_SOURCE_PREFIX}>Literal value…</option>
                        <optgroup label="Form Fields">
                          {fieldNames.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="System Fields">
                          {SPECIAL_FIELDS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    )}
                    {(sourceUsageCounts.get(m.sourceField) ?? 0) > 1 && (
                      <span className={styles.sourceReuseBadge}>
                        (used in {sourceUsageCounts.get(m.sourceField)} rows)
                      </span>
                    )}
                  </td>
                  <td className={styles.mappingTdTransform}>
                    {(() => {
                      const normalized = normalizeTransforms(m.transforms);
                      if (normalized.kind === 'named') {
                        return (
                          <NamedChainPicker
                            value={normalized.namedChainId}
                            onChange={(id) => handleSwitchToNamed(i, id)}
                            onSwitchToInline={(seedSteps) => handleSwitchToInline(i, seedSteps)}
                            onPromoteToCanonical={normalized.namedChainId ? () => startPromote(i, []) : undefined}
                          />
                        );
                      }
                      // inline (or legacy bare array — normalizeTransforms handles both)
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <TransformChipStack
                            chain={normalized.steps}
                            onEdit={() => setDrawerForRowIndex(i)}
                          />
                          {/* F-035 — reuse an existing named chain without re-authoring. */}
                          <UseNamedChainAffordance rowIndex={i} />
                        </div>
                      );
                    })()}
                  </td>
                  <td className={styles.mappingTdArrow}>→</td>
                  <td>
                    {constrained ? (
                      <span className={styles.mappingFixedTarget}>{m.targetColumn}</span>
                    ) : dossierFields ? (
                      // F-036 — pick the remote Dossier doc-type field by key,
                      // showing its label + a required marker. A stored target
                      // not in the schema is preserved as a fallback option.
                      <select
                        className={styles.mappingSelect}
                        value={m.targetColumn}
                        onChange={(e) => handleTargetChange(i, e.target.value)}
                        aria-label={`Target column ${i + 1}`}
                      >
                        <option value="">— select Dossier field —</option>
                        {dossierFields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}{f.required ? ' *required' : ''} ({f.key})
                          </option>
                        ))}
                        {m.targetColumn && !dossierFields.some((f) => f.key === m.targetColumn) && (
                          <option value={m.targetColumn}>
                            {m.targetColumn} (not in Dossier type)
                          </option>
                        )}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className={styles.mappingInput}
                        value={m.targetColumn}
                        onChange={(e) => handleTargetChange(i, e.target.value)}
                        placeholder="Column name"
                        aria-label={`Target column ${i + 1}`}
                      />
                    )}
                    {isRowUnfulfilled(m.targetColumn, slotNameToPath, perSlotByPath) && (
                      <span
                        className={styles.mappingUnfulfilledFlag}
                        data-testid="row-unfulfilled"
                        title="Required slot — not yet fulfilled"
                      >
                        ⚠ required
                      </span>
                    )}
                  </td>
                  <td className={styles.mappingTdFormat}>
                    {/*
                      R-49 / R-50 / WU-2.3: per-row format-override toggle.
                      The badge stays visible when an override is configured
                      so operators can see at a glance which rows opted in
                      without expanding each editor.
                    */}
                    <button
                      type="button"
                      className={
                        hasFormatOverride(m)
                          ? styles.mappingFormatBtnActive
                          : styles.mappingFormatBtn
                      }
                      onClick={() =>
                        setFormatEditorForRowIndex(
                          formatEditorForRowIndex === i ? null : i,
                        )
                      }
                      aria-label={`Format overrides for mapping ${i + 1}`}
                      aria-expanded={formatEditorForRowIndex === i}
                      title="Configure per-row date/number format overrides"
                    >
                      {hasFormatOverride(m) ? 'Format ✓' : 'Format'}
                    </button>
                  </td>
                  <td className={styles.mappingTdDelete}>
                    {/* R-201 — rows are owned by the format when constrained;
                        no per-row delete (edit/clone the format to change columns). */}
                    {!constrained && (
                      <button
                        type="button"
                        className={styles.mappingDeleteBtn}
                        onClick={() => handleDelete(i)}
                        aria-label={`Delete mapping ${i + 1}`}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
                {formatEditorForRowIndex === i && (
                  <tr className={styles.mappingFormatRow}>
                    {/*
                      colSpan covers Kind + Source + Transform + Arrow + Target +
                      Format + Delete (7). Adjust when the header columns
                      change.
                    */}
                    <td colSpan={7}>
                      <MappingRowFormatEditor
                        mapping={m}
                        column={formatColumns?.find((c) => c.name === m.targetColumn) ?? {}}
                        rowIndex={i}
                        onChange={(patch) => handleFormatPatch(i, patch)}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}

        <DestinationWarnings
          mappings={rows}
          fieldNames={fieldNames}
          validationErrors={destinationValidationErrors}
        />
      </div>

      {drawerForRowIndex !== null && (() => {
        const m = rows[drawerForRowIndex]!;
        const normalized = normalizeTransforms(m.transforms);
        // The drawer only opens when the row is in inline mode (user clicked the chip stack).
        const inlineSteps = normalized.kind === 'inline' ? normalized.steps : [];
        return (
          <TransformChainDrawer
            open
            onClose={() => setDrawerForRowIndex(null)}
            chain={inlineSteps}
            sourceFieldName={m.sourceField ?? ''}
            targetColumnName={m.targetColumn ?? ''}
            contextFields={{}}
            onSave={(chain) => handleSaveChain(drawerForRowIndex, chain)}
            onSavedAsNamed={(id) => handleSavedAsNamed(drawerForRowIndex, id)}
            onPromoteToCanonical={(steps) => startPromote(drawerForRowIndex!, steps)}
          />
        );
      })()}

      {promoteState && (
        <PromoteToCanonicalDialog
          open={promoteState !== null}
          state={promoteState}
          sourceFieldName={promoteSourceFieldName}
          newSteps={promoteResolvedSteps}
          existingPostOcr={promoteContext.existingPostOcr}
          otherDestinations={promoteContext.otherDestinations}
          crossFieldOffenders={promoteContext.crossFieldOffenders}
          namedChainError={promoteNamedChainError}
          onClose={closePromote}
          onConfirm={confirmPromote}
          onRetry={retryPromote}
        />
      )}

      {previewCollapsed ? (
        // R-46: thin collapsed rail. A single vertical-text expand button
        // gives operators an obvious affordance to re-open the preview
        // without consuming horizontal space the mapping table needs.
        <div className={styles.previewRail} data-testid="mapping-preview-rail">
          <button
            type="button"
            className={styles.previewRailToggle}
            onClick={() => setPreviewCollapsed(false)}
            aria-label="Show live preview"
            aria-expanded={false}
            title="Show live preview"
          >
            <span aria-hidden="true">‹</span>
            <span className={styles.previewRailLabel}>Live Preview</span>
          </button>
        </div>
      ) : (
        <div className={styles.previewWrap}>
          <button
            type="button"
            className={styles.previewCollapseBtn}
            onClick={() => setPreviewCollapsed(true)}
            aria-label="Hide live preview"
            aria-expanded={true}
            title="Hide live preview"
          >
            ›
          </button>
          <ExportPreview
            mappings={rows}
            format={format}
            structure={structure}
            sampleData={sampleData}
            jobTypeId={jobTypeId}
            columns={formatColumns}
          />
        </div>
      )}

      <ConfirmModal
        open={autoMapConfirmOpen}
        title="Replace current mapping?"
        description="This replaces every mapped row with one column per form field (column = field name). Hand-tuned mappings and transforms will be lost."
        confirmLabel="Replace with auto-map"
        onConfirm={confirmAutoMap}
        onCancel={() => setAutoMapConfirmOpen(false)}
      />
    </div>
  );
}
